import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, CheckCircle, AlertCircle, Clock, FileText, FileCheck, Download } from 'lucide-react';
import { uploadFileToClient, getClient, downloadClientFile } from '../services/api';

interface DocumentUploadProps {
    clientId?: string;
}

type DocumentType = 'equifax' | 'paynet' | 'id';

interface UploadedDocument {
    id: string;
    name: string;
    type: DocumentType;
    size: number;
    status: 'uploading' | 'completed' | 'error';
    error?: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ clientId }) => {
    const [uploadedDocuments, setUploadedDocuments] = useState<Record<DocumentType, UploadedDocument[]>>({
        equifax: [],
        paynet: [],
        id: []
    });
    const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});

    // Load existing documents when clientId is provided
    useEffect(() => {
        const loadExistingDocuments = async () => {
            if (!clientId) {
                return;
            }

            try {
                const response = await getClient(clientId);
                const client = response.client;

                if (client.files && client.files.length > 0) {
                    const documents: Record<DocumentType, UploadedDocument[]> = {
                        equifax: [],
                        paynet: [],
                        id: []
                    };

                    client.files.forEach(file => {
                        if (file.category === 'equifax' || file.category === 'paynet' || file.category === 'id') {
                            documents[file.category as DocumentType].push({
                                id: file.id || file.file_name,
                                name: file.file_name,
                                type: file.category as DocumentType,
                                size: file.file_size || 0,
                                status: 'completed'
                            });
                        }
                    });

                    setUploadedDocuments(documents);
                }
            } catch (error) {
                console.error('Failed to load existing documents:', error);
            }
        };

        loadExistingDocuments();
    }, [clientId]);

    const handleFileUpload = useCallback(async (
        file: File,
        documentType: DocumentType
    ) => {
        if (!clientId) {
            return;
        }

        const fileId = `${documentType}-${Date.now()}-${file.name}`;
        setIsUploading(prev => ({ ...prev, [fileId]: true }));

        // Add to local state immediately
        setUploadedDocuments(prev => ({
            ...prev,
            [documentType]: [...prev[documentType], {
                id: fileId,
                name: file.name,
                type: documentType,
                size: file.size,
                status: 'uploading'
            }]
        }));

        try {
            const fileExtension = file.name.toLowerCase().split('.').pop();
            let fileContent: string;

            if (fileExtension === 'pdf') {
                // Read PDF as binary and convert to base64
                const reader = new FileReader();
                fileContent = await new Promise<string>((resolve, reject) => {
                    reader.onload = (e) => {
                        const arrayBuffer = e.target?.result as ArrayBuffer;
                        if (arrayBuffer) {
                            const uint8Array = new Uint8Array(arrayBuffer);
                            const binary = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
                            const base64Content = btoa(binary);
                            resolve(base64Content);
                        } else {
                            reject(new Error('Error reading file content'));
                        }
                    };
                    reader.onerror = () => reject(new Error('Error reading file'));
                    reader.readAsArrayBuffer(file);
                });
            } else if (fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png') {
                // Read image as binary and convert to base64
                const reader = new FileReader();
                fileContent = await new Promise<string>((resolve, reject) => {
                    reader.onload = (e) => {
                        const arrayBuffer = e.target?.result as ArrayBuffer;
                        if (arrayBuffer) {
                            const uint8Array = new Uint8Array(arrayBuffer);
                            const binary = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
                            const base64Content = btoa(binary);
                            resolve(base64Content);
                        } else {
                            reject(new Error('Error reading file content'));
                        }
                    };
                    reader.onerror = () => reject(new Error('Error reading file'));
                    reader.readAsArrayBuffer(file);
                });
            } else {
                // Try reading as text for other formats
                const reader = new FileReader();
                fileContent = await new Promise<string>((resolve, reject) => {
                    reader.onload = (e) => {
                        const content = e.target?.result as string;
                        if (content) {
                            resolve(content);
                        } else {
                            reject(new Error('Error reading file content'));
                        }
                    };
                    reader.onerror = () => reject(new Error('Error reading file'));
                    reader.readAsText(file);
                });
            }

            const response = await uploadFileToClient(clientId, file.name, fileContent, documentType);

            // Update status to completed and use the file ID from response
            setUploadedDocuments(prev => ({
                ...prev,
                [documentType]: prev[documentType].map(doc =>
                    doc.id === fileId ? { ...doc, status: 'completed', id: response.file_id || doc.id } : doc
                )
            }));
        } catch (error) {
            // Update status to error
            setUploadedDocuments(prev => ({
                ...prev,
                [documentType]: prev[documentType].map(doc =>
                    doc.id === fileId
                        ? { ...doc, status: 'error', error: (error as Error).message }
                        : doc
                )
            }));
        } finally {
            setIsUploading(prev => {
                const newState = { ...prev };
                delete newState[fileId];
                return newState;
            });
        }
    }, [clientId]);

    const handleRemove = (documentType: DocumentType, documentId: string) => {
        setUploadedDocuments(prev => ({
            ...prev,
            [documentType]: prev[documentType].filter(doc => doc.id !== documentId)
        }));
    };

    const handleDownload = async (fileId: string) => {
        if (!clientId) return;

        try {
            await downloadClientFile(clientId, fileId);
        } catch (error) {
            console.error('Failed to download file:', error);
            alert('Failed to download file. Please try again.');
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getStatusIcon = (status: UploadedDocument['status']) => {
        switch (status) {
            case 'uploading':
                return <Clock className="w-4 h-4 text-blue-600 animate-spin" />;
            case 'completed':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'error':
                return <AlertCircle className="w-4 h-4 text-red-600" />;
            default:
                return <FileText className="w-4 h-4 text-gray-400" />;
        }
    };

    const getStatusColor = (status: UploadedDocument['status']) => {
        switch (status) {
            case 'uploading':
                return 'border-blue-200 bg-blue-50';
            case 'completed':
                return 'border-green-200 bg-green-50';
            case 'error':
                return 'border-red-200 bg-red-50';
            default:
                return 'border-gray-200 bg-gray-50';
        }
    };

    const documentTypes: Array<{ type: DocumentType; label: string; description: string; accept: Record<string, string[]> }> = [
        {
            type: 'equifax',
            label: 'Equifax Reports',
            description: 'Upload Equifax credit reports',
            accept: {
                'application/pdf': ['.pdf'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/png': ['.png']
            }
        },
        {
            type: 'paynet',
            label: 'Paynet Reports',
            description: 'Upload Paynet credit reports',
            accept: {
                'application/pdf': ['.pdf'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/png': ['.png']
            }
        },
        {
            type: 'id',
            label: 'ID Documents',
            description: 'Upload identification documents',
            accept: {
                'application/pdf': ['.pdf'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/png': ['.png']
            }
        }
    ];

    const DocumentUploadSection: React.FC<{ docType: typeof documentTypes[0] }> = ({ docType }) => {
        const onDrop = useCallback((acceptedFiles: File[]) => {
            acceptedFiles.forEach(file => {
                handleFileUpload(file, docType.type);
            });
        }, [docType.type]);

        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop,
            accept: docType.accept,
            multiple: true
        });

        const documents = uploadedDocuments[docType.type];
        const hasUploadingFiles = documents.some(d => isUploading[d.id]);

        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-gray-900">{docType.label}</h3>
                        <p className="text-sm text-gray-600">{docType.description}</p>
                    </div>
                    <div className="text-sm text-gray-500">
                        {documents.filter(d => d.status === 'completed').length} uploaded
                    </div>
                </div>

                <div
                    {...getRootProps()}
                    className={`
                        border-2 border-dashed rounded-lg p-6 text-center transition-colors
                        ${hasUploadingFiles
                            ? 'border-blue-400 bg-blue-50 opacity-75 cursor-not-allowed'
                            : isDragActive
                                ? 'border-blue-400 bg-blue-50 cursor-pointer'
                                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
                        }
                    `}
                >
                    <input {...getInputProps()} disabled={hasUploadingFiles} />
                    <div className="flex flex-col items-center gap-2">
                        {hasUploadingFiles ? (
                            <>
                                <Clock className="w-8 h-8 text-blue-600 animate-spin" />
                                <div>
                                    <p className="text-sm font-medium text-blue-900">
                                        Uploading files...
                                    </p>
                                    <p className="text-xs text-blue-600 mt-1">
                                        Please wait while files are being uploaded
                                    </p>
                                </div>
                            </>
                        ) : (
                            <>
                                <Upload className="w-8 h-8 text-gray-400" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        {isDragActive ? 'Drop files here' : `Upload ${docType.label}`}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        PDF, JPG, PNG (drag & drop or click to select)
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {documents.length > 0 && (
                    <div className="space-y-2">
                        {documents.map(doc => (
                            <div
                                key={doc.id}
                                className={`border rounded-lg p-3 ${getStatusColor(doc.status)}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {getStatusIcon(doc.status)}
                                        <span className="text-sm font-medium text-gray-900 truncate">
                                            {doc.name}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {formatFileSize(doc.size)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {doc.status === 'completed' && doc.id && (
                                            <button
                                                onClick={() => handleDownload(doc.id)}
                                                className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                                type="button"
                                                title="Download file"
                                            >
                                                <Download className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleRemove(docType.type, doc.id)}
                                            className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                                            type="button"
                                            title="Remove file"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                {doc.status === 'error' && doc.error && (
                                    <p className="text-xs text-red-600 mt-1">{doc.error}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (!clientId) {
        return (
            <div className="card">
                <div className="p-4 text-center text-gray-500">
                    Please create a client first to upload documents.
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <div className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-blue-600" />
                    <h2 className="card-title">Client Documents</h2>
                </div>
            </div>

            <div className="space-y-6">
                {documentTypes.map(docType => (
                    <DocumentUploadSection key={docType.type} docType={docType} />
                ))}
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Document Upload Guidelines</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Supported formats: PDF, JPG, PNG</li>
                    <li>• Maximum file size: 10MB per file</li>
                    <li>• Upload multiple files by dragging and dropping or clicking to select</li>
                    <li>• Documents are stored securely and associated with this client</li>
                </ul>
            </div>
        </div>
    );
};

export default DocumentUpload;

